import { inject, Injectable } from '@angular/core';
import { Amplify } from 'aws-amplify';
import { fetchAuthSession, signInWithRedirect, signOut } from 'aws-amplify/auth';
import { cognitoUserPoolsTokenProvider } from 'aws-amplify/auth/cognito';
import { catchError, defer, from, interval, map, Observable, of, switchMap, take } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AuthCredentials, AuthSession, AuthUser } from '../../model/app.model';
import { AuthStateService } from './auth-state.service';

const USER_ALREADY_AUTHENTICATED = 'UserAlreadyAuthenticatedException';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly authState = inject(AuthStateService);

  private _refreshTokenInProgress = false;
  private _signInInProgress = false;
  // Offset between this browser's clock and Cognito's, as Amplify measured it. Applied
  // the same way Amplify applies it, so our expiry checks can't disagree with the ones
  // guarding its token store.
  private _clockDriftMs = 0;

  constructor() {
    this.configure();

    interval(environment.awsCognitoConfig.tokenRefreshCheckPeriod)
      .pipe(switchMap(() => this.readClockDrift()))
      .subscribe((clockDriftMs) => {
        this._clockDriftMs = clockDriftMs;
        this.checkAndTryRefreshToken();
      });
  }

  private configure(): void {
    const cfg = environment.awsCognitoConfig;
    Amplify.configure({
      Auth: {
        Cognito: {
          identityPoolId: cfg.identityPoolId,
          userPoolId: cfg.userPoolId,
          userPoolClientId: cfg.userPoolWebClientId,
          loginWith: {
            oauth: {
              domain: cfg.oauth.domain,
              scopes: cfg.oauth.scope,
              redirectSignIn: [cfg.oauth.redirectSignIn],
              redirectSignOut: [cfg.oauth.redirectSignOut],
              responseType: cfg.oauth.responseType as 'code' | 'token',
            },
          },
        },
      },
    });
  }

  isAuthenticatedGetAuthSession(): Observable<AuthSession | undefined> {
    return from(fetchAuthSession()).pipe(
      map((session) => {
        if (!session.tokens || !session.credentials) {
          return undefined;
        }

        const payload = session.tokens.idToken?.payload ?? {};

        const user: AuthUser = {
          idToken: {
            jwtToken: session.tokens.idToken?.toString() ?? '',
            expiration: (payload['exp'] as number) ?? 0,
            issuedAt: (payload['iat'] as number) ?? 0,
          },
          groups: (payload['cognito:groups'] as string[]) ?? [],
          userType: (payload['custom:user_type'] as string) ?? '',
          vendor: (payload['custom:vendor'] as string) ?? '',
        };

        const credentials: AuthCredentials = {
          accessKeyId: session.credentials.accessKeyId,
          secretAccessKey: session.credentials.secretAccessKey,
          sessionToken: session.credentials.sessionToken ?? '',
          identityId: session.identityId ?? '',
          authenticated: true,
          expiration: session.credentials.expiration,
        };

        return { credentials, user };
      }),
      catchError((err) => {
        console.error('Auth session error', err);
        return of(undefined);
      }),
    );
  }

  federatedSignIn(): void {
    // The refresh interval and every failing request funnel in here, so without this
    // the hosted-UI redirect gets re-requested several times a second
    if (this._signInInProgress) {
      return;
    }
    this._signInInProgress = true;

    signInWithRedirect().catch((err: unknown) => {
      // Amplify asserts no user is signed in before it navigates anywhere, and its own
      // check only looks at tokens — never at the identity-pool credentials we also
      // require. Whenever the two disagree this is the error, and it is thrown before
      // the redirect, so recovering here is what stops the app sitting on a dead
      // session logging this forever instead of returning to log in.
      if (err instanceof Error && err.name === USER_ALREADY_AUTHENTICATED) {
        this.recoverFromStaleAmplifySession();
        return;
      }

      this._signInInProgress = false;
      console.error('[Auth] OAuth sign-in error', err);
    });
  }

  private recoverFromStaleAmplifySession(): void {
    console.warn(
      '[Auth] Amplify still reports a signed-in user — clearing it to force a fresh sign-in',
    );
    this.authState.clearSession();

    // signOut() clears the local token store before redirecting to Cognito's logout
    // endpoint, which lands back on redirectSignOut and re-runs authGuard. Only that
    // redirect can fail here, and by then the tokens are already gone — so the retry
    // passes Amplify's assertion instead of tripping it again.
    signOut().catch((err) => {
      console.error('[Auth] sign-out during stale-session recovery failed', err);
      this._signInInProgress = false;
      this.federatedSignIn();
    });
  }

  signOut(): Observable<void> {
    return from(signOut()).pipe(take(1));
  }

  private checkAndTryRefreshToken(): void {
    const session = this.authState.session();
    if (this._refreshTokenInProgress || !this.authState.isAuthenticated() || !session) {
      return;
    }

    const now = this.serverNow();
    const expiration = session.user.idToken.expiration * 1000;
    const issuedAt = session.user.idToken.issuedAt * 1000;
    const duration = expiration - issuedAt;
    const expiresIn = expiration - now;
    const expiresForRefreshIn =
      issuedAt + duration * environment.awsCognitoConfig.tokenDurationSafeBufferFactor - now;

    if (expiresIn <= 0) {
      // Drop the session before redirecting: authGuard short-circuits on
      // isAuthenticated() and the interceptor keeps signing requests with these
      // credentials for as long as it is left in place
      this.authState.clearSession();
      this.federatedSignIn();
    } else if (expiresForRefreshIn < 0) {
      this.refreshToken();
    }
  }

  // Cognito's clock rather than this machine's. Amplify serves tokens from its store
  // based on Date.now() + clockDrift, so measuring expiry any other way lets us call a
  // session dead while Amplify still considers it live. A clock running ahead of Cognito
  // by more than the token lifetime puts us there immediately after every sign-in.
  private serverNow(): number {
    return Date.now() + this._clockDriftMs;
  }

  private readClockDrift(): Observable<number> {
    // defer so an unset token store surfaces as a caught error rather than a synchronous
    // throw that would tear down the refresh interval for the rest of the session
    return defer(() =>
      cognitoUserPoolsTokenProvider.tokenOrchestrator.getTokenStore().loadTokens(),
    ).pipe(
      map((tokens) => tokens?.clockDrift ?? 0),
      catchError(() => of(0)),
    );
  }

  private refreshToken(): void {
    this._refreshTokenInProgress = true;
    from(fetchAuthSession({ forceRefresh: true }))
      .pipe(
        take(1),
        switchMap(() => this.isAuthenticatedGetAuthSession()),
        take(1),
      )
      .subscribe({
        next: (session) => {
          if (session) {
            this.authState.setSession(session);
          } else {
            // isAuthenticatedGetAuthSession swallows its own failures into undefined,
            // so a credentials lookup that fell over arrives here, not in error
            this.handleFailedRefresh();
          }
          this._refreshTokenInProgress = false;
        },
        error: (err) => {
          console.error('[Auth] token refresh failed', err);
          this.handleFailedRefresh();
          this._refreshTokenInProgress = false;
        },
      });
  }

  private handleFailedRefresh(): void {
    // Amplify deliberately keeps its tokens through transient failures (network blips,
    // Cognito 5xx, rate limits), so while the current one is still valid keep ours too
    // and retry on the next tick rather than bouncing the user to log in. Past expiry it
    // has to go — a stale session left in place is what leaves authGuard short-circuiting
    // on dead credentials with no path back to the login page.
    const session = this.authState.session();
    if (!session || session.user.idToken.expiration * 1000 - this.serverNow() <= 0) {
      this.authState.clearSession();
    }
  }
}
